import React, { useState, useEffect, useRef } from "react";
import { styled, createGlobalStyle } from "styled-components";
import { useLocation } from "react-router-dom";
import GenerateModal from "./GenerateModal";
import { diffWords } from "diff";
import {
  createArticle,
  getArticle,
  updateArticle,
  createArticleVersion,
  getArticleVersion,
  getPreviousVersion,
  getDisplayStatus,
  getLatestArticleVersion,
  updateArticleVersionScores,
} from "../../lib/articles";
import { reviseSelection } from "./generateContent";
import { checkMarkupAIAll } from "./checkMarkupAI";
import FileManager from "../FileManager";
import supabase from "../../supabase";
import { createComment } from "../../lib/comments";
import { sendNotification, notifyTeam } from "../../lib/notifications";

function hasRealContent(html) {
  if (!html) return false;
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim().length > 0;
}

const TextBoxWrapper = styled.div`
  flex: 1 1 75%;
  max-width: 75%;
  padding-right: 20px;
  min-width: 0;
  box-sizing: border-box;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0px;
  margin-top: 10px;
`;

const ToolbarButton = styled.button`
  padding: 5px 10px;
  cursor: pointer;
`;

const EditableDiv = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  height: 100vh;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  background: white;
  overflow-y: auto;
  line-height: 1.5;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  &:empty:before {
    content: attr(data-placeholder);
    color: #888;
  }

  img,
  table,
  iframe,
  video {
    max-width: 100%;
  }
  pre,
  code {
    white-space: pre-wrap; /* wrap code if pasted */
    word-break: break-word;
  }

  ul {
    list-style-type: disc !important;
    margin: 1em 0 !important;
    padding-left: 40px !important;
  }

  ol {
    list-style-type: decimal;
    margin: 1em 0;
    padding-left: 40px;
  }

  a {
    color: #00876e;
    text-decoration: underline;
    cursor: pointer;
  }
`;

// simple highlight style for shown comments
const HighlightStyle = () => (
  <style>
    {`.comment-highlight,
    .improvement-highlight { background: #fde68a; border-radius: 2px; padding: 0 2px; }
    .ai-proposed-change { background: #dcfce7; color: #166534; border-bottom: 2px solid #22c55e; cursor: pointer; }`}
  </style>
);

const DiffStyles = createGlobalStyle`
   .diff-added { background:#dcfce7; border-radius:2px; padding:0 2px; }
   .diff-removed { background:#fee2e2; color:#991b1b; text-decoration: line-through; border-radius:2px; padding:0 2px; }
 `;

const TextBox = ({
  setContent,
  analyzeContent,
  generateContent,
  setFeedback,
  feedback,
  beginGenerating,
  endGenerating,
  onArticleContextChange,
  onUnsavedDraftChange,
  profile,
  projects,
  pendingVersion,
  onVersionApplied,
  currentUserRole,
  onImprovementApplied,
  setBrief,
  brief,
  organization,
}) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [metaTags, setMetaTags] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const editableDivRef = useRef(null);
  const savedRangeRef = useRef(null);
  const metaTagsDivRef = useRef(null);
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  const [currentArticleId, setCurrentArticleId] = useState(null); // null means “new draft”
  const [currentVersionId, setCurrentVersionId] = useState(null); // which version's score row Analyze should update
  const [currentTitle, setCurrentTitle] = useState("Untitled Article");
  const [editorMode, setEditorMode] = useState("draft"); // 'draft' | 'existing'
  const [lastSavedHTML, setLastSavedHTML] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [autosaveFlash, setAutosaveFlash] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [pendingSelection, setPendingSelection] = useState(null);
  const [diffActiveForVersion, setDiffActiveForVersion] = useState(null);
  const [preDiffHtml, setPreDiffHtml] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const autosaveFlashTimer = useRef(null);
  const [progress, setProgress] = useState("In Progress");
  const [llmProvider, setLlmProvider] = useState("openai"); // 'openai' | 'anthropic'
  const [improvementTip, setImprovementTip] = useState(null);
  const [selectionAction, setSelectionAction] = useState(null);
  const [reviseTip, setReviseTip] = useState(null);
  const [reviseInstruction, setReviseInstruction] = useState("");
  const [isRevising, setIsRevising] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [fontSizePx, setFontSizePx] = useState(16);
  const [linkedRequestId, setLinkedRequestId] = useState(null);
  const [targetOrgId, setTargetOrgId] = useState(null);

  // ✅ New state to manage the Accept/Revert flow
  const [revisionWidget, setRevisionWidget] = useState(null); // { id, originalSpanContent, top, left }

  const currentHighlightRef = useRef(null);

  const location = useLocation();

  const loadedRouteKey = useRef(null);

  // Local state for brief in case parent doesn't hold it
  const [localBrief, setLocalBrief] = useState(null);

  const userRole = currentUserRole.currentUserRole;

  const debounceRef = useRef();
  const debounce = (fn, ms = 1500) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fn, ms);
  };

  // helper to broadcast context up whenever it changes
  const emitCtx = (id, title) => {
    onArticleContextChange?.({ id, title });
  };

  // ✅ FULL INITIALIZATION from Navigation State (Dashboard / New Article / Request Details)
  useEffect(() => {
    const stateArticle = location.state?.openArticle;

    if (!stateArticle || loadedRouteKey.current === location.key) return;
    loadedRouteKey.current = location.key;

    const loadRoutedArticle = async () => {
      let finalArticle = stateArticle;

      // ✅ FIX: If the router only gave us an ID (e.g. clicking "Open Existing Article"), fetch the full record!
      if (
        stateArticle.id &&
        !stateArticle.content_html &&
        !stateArticle.title
      ) {
        try {
          const fetched = await getArticle(stateArticle.id);
          if (fetched) {
            finalArticle = fetched;
          }
        } catch (err) {
          console.error("Error fetching full article details:", err);
        }
      }

      window.dispatchEvent(new CustomEvent("versions:clearChanges"));
      setEditorMode(finalArticle.id ? "existing" : "draft");
      setCurrentArticleId(finalArticle.id || null);
      setCurrentVersionId(null); // resolved lazily (latest version) on next Analyze
      if (finalArticle.id) {
        localStorage.setItem("currentArticleId", finalArticle.id);
      } else {
        localStorage.removeItem("currentArticleId");
      }
      setCurrentTitle(finalArticle.title || "Untitled Article");
      setCurrentProjectId(finalArticle.project_id || null);
      emitCtx(finalArticle.id, finalArticle.title || "Untitled Article");

      if (finalArticle.progress) setProgress(finalArticle.progress);

      setFeedback?.({
        overall: finalArticle.score ?? null,
        readability: finalArticle.readability_score ?? null,
        topicAuthority: finalArticle.topic_authority_score ?? null,
        contentValue: finalArticle.content_value_score ?? null,
        seo: finalArticle.seo_score ?? null,
        improvements: null,
      });

      const html = finalArticle.content_html || "";
      if (editableDivRef.current) {
        editableDivRef.current.innerHTML = html;
      }
      setHtmlContent(html);
      setContent(html);
      localStorage.setItem("textBoxContent", html);
      setLastSavedHTML(html);

      if (finalArticle.meta_tags) {
        const metaTagsString = finalArticle.meta_tags
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        if (metaTagsDivRef.current)
          metaTagsDivRef.current.innerHTML = metaTagsString;
        localStorage.setItem("metaTagsContent", metaTagsString);
      } else {
        if (metaTagsDivRef.current) metaTagsDivRef.current.innerHTML = "";
        localStorage.removeItem("metaTagsContent");
      }

      const b = finalArticle.content_brief || null;
      setLocalBrief(b);
      if (setBrief) setBrief(b);
      window.dispatchEvent(new CustomEvent("brief:update", { detail: b }));

      if (finalArticle.id) {
        window.dispatchEvent(
          new CustomEvent("versions:refresh", {
            detail: { article_id: finalArticle.id },
          })
        );
      }

      // Auto-open the generate modal if navigating from a Request with the flag
      if (location.state?.autoOpenGenerate) {
        setIsModalOpen(true);
      }

      // Catch the request ID if we are creating a new article from a request
      if (location.state?.linkedRequestId) {
        setLinkedRequestId(location.state.linkedRequestId);
      } else {
        setLinkedRequestId(null);
      }

      if (finalArticle.org_id) {
        setTargetOrgId(finalArticle.org_id);
      } else {
        setTargetOrgId(null);
      }
    };

    loadRoutedArticle();
  }, [location.state, location.key, setBrief]);

  // Safety: ensure parent/sidebar always knows what article is open
  useEffect(() => {
    if (!currentArticleId) return;
    emitCtx(currentArticleId, currentTitle || "Untitled Article");
  }, [currentArticleId, currentTitle]);

  const flashAutosaved = () => {
    setAutosaveFlash(true);
    clearTimeout(autosaveFlashTimer.current);
    autosaveFlashTimer.current = setTimeout(() => {
      setAutosaveFlash(false);
    }, 2000);
  };

  const handleTextChange = () => {
    const text = editableDivRef.current.innerHTML;
    setHtmlContent(text);
    setContent(text);
    localStorage.setItem("textBoxContent", text);

    if (currentArticleId && text !== lastSavedHTML) {
      debounce(async () => {
        try {
          setIsAutosaving(true);
          await updateArticle(currentArticleId, {
            title: currentTitle,
            content_html: text,
            meta_tags: feedback?.metaTags || "",
          });
          setLastSavedHTML(text);
          flashAutosaved();
        } catch (e) {
          console.error("Autosave failed:", e);
        } finally {
          setIsAutosaving(false);
        }
      }, 1500);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("text/html");

    if (document.queryCommandSupported("insertHTML")) {
      document.execCommand("insertHTML", false, pastedData);
    } else {
      const text = clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editableDivRef.current.focus();
  };

  const handleInsertLink = () => {
    saveSelection();
    const url = window.prompt("Enter the link URL:", "https://");

    // If user provided a URL and didn't cancel
    if (url) {
      restoreSelection();
      execCommand("createLink", url);

      // Optional: Force the link to open in a new tab
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.focusNode;
        if (node && node.nodeType === 3) node = node.parentNode; // Get parent element if text node
        if (node && node.tagName === "A") {
          node.setAttribute("target", "_blank");
          node.setAttribute("rel", "noopener noreferrer");
        }
      }
      handleTextChange(); // Trigger autosave
    }
  };

  // ✅ Add Unlink Handler
  const handleRemoveLink = () => {
    saveSelection();
    restoreSelection();
    execCommand("unlink");
    handleTextChange();
  };

  const applyFontSize = (px) => {
    const editor = editableDivRef.current;
    if (!editor) return;
    setFontSizePx(px);
    editor.focus();
    const restored = restoreSelection();
    if (!restored) {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
        return;
      }
    }
    try {
      document.execCommand("styleWithCSS", false, false);
    } catch (e) {
      console.warn("styleWithCSS command failed", e);
    }
    document.execCommand("fontSize", false, "7");
    const fonts = editor.querySelectorAll('font[size="7"]');
    fonts.forEach((fontEl) => {
      if (fontEl.getAttribute("data-fs-done")) return;
      fontEl.setAttribute("data-fs-done", "1");
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      span.style.lineHeight = "1.35";
      while (fontEl.firstChild) {
        span.appendChild(fontEl.firstChild);
      }
      if (fontEl.parentNode) {
        fontEl.parentNode.replaceChild(span, fontEl);
      }
    });
    saveSelection();
    handleTextChange();
  };

  const handleEditorMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setSelectionAction(null);
      setSelectedRange(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const selectedText = sel.toString().trim();
    if (!selectedText) {
      setSelectionAction(null);
      setSelectedRange(null);
      return;
    }
    if (!editableDivRef.current?.contains(range.commonAncestorContainer)) {
      setSelectionAction(null);
      setSelectedRange(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    setSelectedRange(range.cloneRange());
    setSelectionAction({
      top: rect.top - 10,
      left: rect.right + 8,
      exactText: selectedText,
      occurrenceIndex: 0,
    });
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editableDivRef.current?.contains(range.commonAncestorContainer))
      return;
    savedRangeRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return false;
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  };

  const handleDownload = () => {
    // 1. Create a temporary clone of the editor to clean it up safely
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = editableDivRef.current?.innerHTML || "";

    // 2. Strip out the confusing <font size="7"> tags our font tool creates
    const fonts = tempDiv.querySelectorAll('font[size="7"]');
    fonts.forEach((font) => {
      const wrapper = document.createElement("span");
      wrapper.innerHTML = font.innerHTML;
      font.replaceWith(wrapper);
    });

    // ✅ NEW: The "Heading Inside List" Decoy
    // Convert official heading tags inside lists to styled divs to stop the parser from breaking
    const listHeadings = tempDiv.querySelectorAll("li h1, li h2, li h3, li h4");
    listHeadings.forEach((heading) => {
      const fakeHeading = document.createElement("div");
      fakeHeading.innerHTML = heading.innerHTML;
      fakeHeading.style.fontWeight = "bold";
      fakeHeading.style.color = "#000000";
      fakeHeading.style.marginTop = "10pt";
      fakeHeading.style.marginBottom = "4pt";

      if (heading.tagName === "H1") fakeHeading.style.fontSize = "24pt";
      else if (heading.tagName === "H2") fakeHeading.style.fontSize = "18pt";
      else if (heading.tagName === "H3") fakeHeading.style.fontSize = "14pt";
      else if (heading.tagName === "H4") fakeHeading.style.fontSize = "12pt";

      heading.replaceWith(fakeHeading);
    });

    // 3. Stop Heading styles from bleeding into paragraphs inside lists
    const paragraphs = tempDiv.querySelectorAll("p");
    paragraphs.forEach((p) => {
      p.style.fontWeight = "normal";
      p.style.fontSize = "12pt";
      p.style.marginTop = "0pt";
      p.style.marginBottom = "10pt";
    });

    // 4. INTELLIGENT FILENAME GENERATOR
    let extractedTitle = currentTitle;

    if (!extractedTitle || extractedTitle === "Untitled Article") {
      const h1 = tempDiv.querySelector("h1");
      if (h1 && h1.innerText.trim()) {
        extractedTitle = h1.innerText.trim();
      } else {
        extractedTitle = "Article_Export";
      }
    }

    const safeFilename = extractedTitle
      .replace(/[/\\?%*:|"<>]/g, "")
      .replace(/\s+/g, "_")
      .substring(0, 60);

    const cleanContent = tempDiv.innerHTML;

    // 5. Inject strict CSS
    const styles = `
      <style>
        body, p, li { 
          font-family: Arial, sans-serif; 
          font-size: 12pt; 
          font-weight: normal; 
          line-height: 1.5; 
          color: #000000; 
        }
        h1, h2, h3, h4 { 
          font-family: Arial, sans-serif; 
          color: #000000; 
        }
        h1 { font-size: 24pt; font-weight: bold; margin-top: 12pt; margin-bottom: 6pt; }
        h2 { font-size: 18pt; font-weight: bold; margin-top: 10pt; margin-bottom: 4pt; }
        h3 { font-size: 14pt; font-weight: bold; margin-top: 8pt; margin-bottom: 4pt; }
        h4 { font-size: 12pt; font-weight: bold; margin-top: 6pt; margin-bottom: 2pt; }
        p { margin-top: 0; margin-bottom: 10pt; }
        ul, ol { margin-top: 0; margin-bottom: 10pt; padding-left: 40px; }
        li { margin-bottom: 4pt; }
        a { color: #1155cc; text-decoration: underline; }
      </style>
    `;

    // 6. Wrap it in the Microsoft Office namespaces
    const preHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>${extractedTitle}</title>
        ${styles}
      </head>
      <body>`;
    const postHtml = "</body></html>";

    const html = preHtml + cleanContent + postHtml;

    // 7. Trigger the download
    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFilename}.doc`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAnalyzeClick = async () => {
    try {
      const currentHtml = editableDivRef.current?.innerHTML ?? htmlContent;
      beginGenerating?.(
        "Analyzing your content... This may take a few minutes."
      );
      const results = await analyzeContent(currentHtml, setFeedback, llmProvider);
      const overallScore = results?.["Overall Grade"];
      if (overallScore != null && currentArticleId) {
        const scores = {
          score: overallScore,
          readability_score: results["Readability Grade"] ?? null,
          topic_authority_score: results["Topic Authority Grade"] ?? null,
          content_value_score: results["Content Value Grade"] ?? null,
          seo_score: results["SEO Grade"] ?? null,
        };
        await updateArticle(currentArticleId, scores);

        // Also snapshot onto the version currently open, so reopening it
        // later shows the scores it was actually analyzed with — not just
        // whatever the next-saved version happens to capture.
        try {
          let versionId = currentVersionId;
          if (!versionId) {
            const latest = await getLatestArticleVersion(currentArticleId);
            versionId = latest?.id || null;
            if (versionId) setCurrentVersionId(versionId);
          }
          if (versionId) {
            await updateArticleVersionScores(versionId, scores);
          }
        } catch (err) {
          console.error("Failed to snapshot scores onto version:", err);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      endGenerating?.();
    }
  };

  const handleGenerateClick = () => {
    setEditorMode("draft");
    setCurrentArticleId(null);
    setCurrentVersionId(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleNewArticle = async () => {
    if (isUnsavedNewDraft) {
      const shouldSave = window.confirm(
        "You have an unsaved article. Save it before starting a new one?"
      );
      if (shouldSave) {
        await onSaveArticle("saveAs");
      }
    }

    window.dispatchEvent(new CustomEvent("versions:clearChanges"));
    setEditorMode("draft");
    setCurrentArticleId(null);
    setCurrentVersionId(null);
    localStorage.removeItem("currentArticleId");
    setCurrentTitle("Untitled Article");
    setCurrentProjectId(null);
    setLinkedRequestId(null);
    setTargetOrgId(null);
    setProgress("In Progress");

    if (editableDivRef.current) editableDivRef.current.innerHTML = "";
    setHtmlContent("");
    setContent("");
    localStorage.setItem("textBoxContent", "");
    setLastSavedHTML("");

    setLocalBrief(null);
    if (setBrief) setBrief(null);
    window.dispatchEvent(new CustomEvent("brief:update", { detail: null }));

    setFeedback?.({
      overall: null,
      readability: null,
      topicAuthority: null,
      contentValue: null,
      seo: null,
      improvements: null,
    });

    emitCtx(null, "Untitled Article");
  };

  const handleModalSubmit = async (formData) => {
    try {
      beginGenerating?.(
        "Generating your article...\nThis may take a few minutes."
      );

      // Update brief state locally and broadcast
      setLocalBrief(formData);
      if (setBrief) setBrief(formData);
      window.dispatchEvent(
        new CustomEvent("brief:update", { detail: formData })
      );

      const generatedHtml = await generateContent(
        formData,
        setFeedback,
        (msg) => beginGenerating?.(msg),
        llmProvider
      );
      if (editableDivRef.current) {
        editableDivRef.current.innerHTML = generatedHtml.Article;
      }
      setHtmlContent(generatedHtml.Article);
      localStorage.setItem("textBoxContent", generatedHtml.Article);
      setEditorMode("draft");
      setCurrentArticleId(null);
      setCurrentVersionId(null);
      localStorage.removeItem("currentArticleId");
      setCurrentTitle(formData?.title || "Untitled Article");
      setCurrentProjectId(formData?.project_id ?? null);
      setLastSavedHTML("");
      emitCtx?.(null, formData?.title || "Untitled Article");
    } catch (e) {
      console.error("Error generating content:", e);
    } finally {
      endGenerating?.();
      setIsModalOpen(false);
    }
  };

  const onSaveArticle = async (mode = "save") => {
    try {
      const content_html = editableDivRef.current?.innerHTML || "";
      const meta_tags = feedback?.metaTags || "";
      const content_brief = localBrief || brief || null;
      let title = (currentTitle || "Untitled Article").trim();
      // Real AI scores are always 1-100, so 0 only ever means "never
      // analyzed" (the feedback state's untouched default) — store null
      // rather than a misleading 0 for this version's score snapshot.
      const versionScores = {
        score: feedback?.overall || null,
        readability_score: feedback?.readability || null,
        topic_authority_score: feedback?.topicAuthority || null,
        content_value_score: feedback?.contentValue || null,
        seo_score: feedback?.seo || null,
      };

      if (mode === "saveAs") {
        const input = window.prompt("Enter a title for this article:", title);
        if (input === null) return;
        title = (input || "Untitled Article").trim();

        const id = await createArticle({
          title,
          content_html,
          meta_tags,
          content_brief,
          project_id: currentProjectId || null,
          org_id: profile?.company_id ?? null,
          client_org_id: targetOrgId || null,
        });

        if (linkedRequestId) {
          await supabase
            .from("Requests")
            .update({ article_id: id, status: "In Progress" })
            .eq("id", linkedRequestId);

          setLinkedRequestId(null); // Clear it so we don't accidentally link again
        }

        setCurrentArticleId(id);
        localStorage.setItem("currentArticleId", id);
        setEditorMode("existing");
        setCurrentTitle(title);
        setLastSavedHTML(content_html);
        emitCtx?.(id, title);

        const newVersion = await createArticleVersion({
          article_id: id,
          title,
          content_html,
          meta_tags,
          ...versionScores,
        });
        setCurrentVersionId(newVersion?.id || null);

        window.dispatchEvent(
          new CustomEvent("versions:refresh", {
            detail: { article_id: id },
          })
        );
        alert("Saved as new article.");
        return;
      }

      if (!currentArticleId) {
        alert(
          "This is a new draft. Use 'Save As' to create the first saved copy."
        );
        return;
      }

      await updateArticle(currentArticleId, {
        title,
        content_html,
        meta_tags,
        content_brief,
      });
      setLastSavedHTML(content_html);
      flashAutosaved();
      emitCtx?.(currentArticleId, title);

      const newVersion = await createArticleVersion({
        article_id: currentArticleId,
        title,
        content_html,
        meta_tags,
        ...versionScores,
      });
      setCurrentVersionId(newVersion?.id || null);

      window.dispatchEvent(
        new CustomEvent("versions:refresh", {
          detail: { article_id: currentArticleId },
        })
      );
      alert("New Article Version Created.");
    } catch (e) {
      console.error(e);
      alert("Failed to save. Check console.");
    }
  };

  const isUnsavedNewDraft = !currentArticleId && hasRealContent(htmlContent);

  // Reports the current draft's save state upward (to OrganizationPortal) so
  // sidebar/profile-menu navigation can prompt to save before leaving.
  useEffect(() => {
    onUnsavedDraftChange?.({
      hasUnsaved: isUnsavedNewDraft,
      saveAs: () => onSaveArticle("saveAs"),
    });
  }, [isUnsavedNewDraft]);

  // Clears the reported draft state when the editor actually unmounts, so
  // leaving the page (however the user answered the prompt) stops further
  // prompts until a new unsaved draft exists.
  useEffect(() => {
    return () => {
      onUnsavedDraftChange?.({ hasUnsaved: false, saveAs: null });
    };
  }, []);

  // Best-effort backstop for tab close/refresh/typed-URL navigation, which
  // can't run an async save — the browser can only show its own generic
  // warning here.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUnsavedNewDraft) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUnsavedNewDraft]);

  const handleStatusNotification = async (articleId, newStatus) => {
    console.log(
      "🔔 [Notify] 1. Triggered for Article ID:",
      articleId,
      "New Status:",
      newStatus
    );

    try {
      const { data: article, error: artErr } = await supabase
        .from("articles")
        .select("title, created_by, project_id, org_id")
        .eq("id", articleId)
        .single();

      if (artErr) console.error("🔔 [Notify] ERROR fetching article:", artErr);
      if (!article)
        return console.log("🔔 [Notify] ABORT: Article not found in DB.");

      console.log(
        "🔔 [Notify] 2. Article fetched. Project ID is:",
        article.project_id
      );

      let targetUserIds = [];
      const subject = `Status Update: ${article.title}`;
      const body = `The status of the article "${article.title}" has been updated to "${newStatus}".`;

      let project = null;
      if (article.project_id) {
        const { data: pData, error: projErr } = await supabase
          .from("projects")
          .select("created_by, editors, approvers")
          .eq("id", article.project_id)
          .single();

        if (projErr)
          console.error("🔔 [Notify] ERROR fetching project:", projErr);
        project = pData;
        console.log(
          "🔔 [Notify] 3. Project fetched. Editors array:",
          project?.editors
        );
      } else {
        console.log(
          "🔔 [Notify] WARNING: This article has no project_id. Team routing will fail."
        );
      }

      if (newStatus === "Ready for Review") {
        if (project?.editors && Array.isArray(project.editors)) {
          targetUserIds.push(...project.editors);
        }
      } else if (newStatus === "Edits Needed") {
        if (article.created_by) targetUserIds.push(article.created_by);
      } else if (newStatus === "Ready for Approval") {
        if (project?.approvers && Array.isArray(project.approvers)) {
          targetUserIds.push(...project.approvers);
        }
      } else if (newStatus === "Ready to Publish") {
        if (project?.created_by) targetUserIds.push(project.created_by);
        if (article.org_id) {
          const { data: admins } = await supabase
            .from("Users")
            .select("id")
            .eq("company_id", article.org_id)
            .eq("role", "Admin");
          if (admins) targetUserIds.push(...admins.map((a) => a.id));
        }
      }

      console.log(
        "🔔 [Notify] 4. Target User IDs before filtering:",
        targetUserIds
      );

      if (targetUserIds.length > 0) {
        await notifyTeam({
          user_ids: targetUserIds,
          subject,
          body,
          article_id: articleId,
          project_id: article.project_id,
        });
      } else {
        console.log(
          "🔔 [Notify] ABORT: No users found to notify for this status."
        );
      }
    } catch (err) {
      console.error("🔔 [Notify] CRITICAL FAILURE:", err);
    }
  };

  // ✅ UPDATED: Call the notification helper after updating the article
  const updateProgress = async (status) => {
    if (!currentArticleId) {
      alert("Save the article first before changing its status.");
      return;
    }
    try {
      await updateArticle(currentArticleId, { progress: status });
      await supabase
        .from("Requests")
        .update({ status: status })
        .eq("article_id", currentArticleId);
      setProgress(status);
      setStatusMessage(`Status updated to "${status}"`);
      setTimeout(() => setStatusMessage(""), 3000);

      // Trigger the notifications asynchronously
      await handleStatusNotification(currentArticleId, status);
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const canSee = (buttonName) => {
    const role = (currentUserRole || "").toLowerCase();
    switch (buttonName) {
      case "Ready for Review":
        return ["admin", "writer", "editor"].includes(role);
      case "Ready for Approval":
        return ["admin", "editor"].includes(role);
      case "Ready to Publish":
        return ["admin", "approver"].includes(role);
      default:
        return false;
    }
  };

  // Non-ai_enabled orgs skip the "Ready for Review" checkpoint entirely —
  // their pipeline only ever moves In Progress -> Edits Needed / Ready for
  // Approval / Ready to Publish.
  const statusOptions = [
    { value: "In Progress", visible: true },
    {
      value: "Ready for Review",
      visible: organization?.ai_enabled && canSee("Ready for Review"),
    },
    { value: "Ready for Approval", visible: canSee("Ready for Approval") },
    { value: "Ready to Publish", visible: canSee("Ready to Publish") },
    { value: "Edits Needed", visible: currentUserRole !== "Writer" },
  ].filter((o) => o.visible);

  const getSelectionInEditor = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!editableDivRef.current) return null;
    if (!editableDivRef.current.contains(range.commonAncestorContainer))
      return null;

    const exactText = sel.toString().trim();
    if (!exactText) return null;

    const full = editableDivRef.current.innerText || "";
    let idx = 0;
    let count = 0;
    while (true) {
      const found = full.indexOf(exactText, idx);
      if (found === -1) break;
      if (found < full.indexOf(exactText)) count++;
      idx = found + exactText.length;
    }
    const occurrenceIndex = 0;
    return { exactText, occurrenceIndex };
  };

  const clearHighlights = () => {
    const root = editableDivRef.current;
    if (!root) return;
    const marks = root.querySelectorAll(
      ".comment-highlight, .improvement-highlight"
    );
    marks.forEach((el) => {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      parent.normalize();
    });
  };

  const highlightExactText = (
    needle,
    nth = 0,
    className = "comment-highlight"
  ) => {
    if (!editableDivRef.current || !needle) return false;
    clearHighlights();
    const { plain, posToLoc } = flattenEditorText(editableDivRef.current);
    const { norm: hayNorm, map: hayMap } = normalizeWithMap(plain);
    const { norm: ndlNorm } = normalizeWithMap(needle);

    if (!ndlNorm.length) return false;

    let startIdx = -1;
    let searchFrom = 0;
    let foundCount = 0;

    while (true) {
      startIdx = hayNorm.indexOf(ndlNorm, searchFrom);
      if (startIdx === -1) break;
      if (foundCount === nth) break;
      foundCount++;
      searchFrom = startIdx + 1;
    }

    if (startIdx === -1) return false;
    const origStart = hayMap[startIdx];
    const origEndExclusive = hayMap[startIdx + ndlNorm.length - 1] + 1;
    const startLoc = posToLoc[origStart];
    const endLoc = posToLoc[origEndExclusive - 1];
    if (!startLoc || !endLoc) return false;

    const range = document.createRange();
    range.setStart(startLoc.node, startLoc.offset);
    range.setEnd(endLoc.node, endLoc.offset + 1);

    const span = document.createElement("span");
    span.className = className;
    span.style.background = "#fde68a";
    span.style.borderRadius = "2px";
    span.style.padding = "0 2px";
    try {
      range.surroundContents(span);
    } catch {
      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    return span;
  };

  const scrollSpanIntoEditor = (span, { smooth = true } = {}) => {
    const editor = editableDivRef.current;
    if (!editor || !span) return;
    const editorRect = editor.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    const offsetInEditor = spanRect.top - editorRect.top;
    const targetScrollTop =
      editor.scrollTop + offsetInEditor - editor.clientHeight / 2;
    editor.scrollTo({
      top: targetScrollTop,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  const restoreEditorFromSnapshot = () => {
    if (preDiffHtml != null && editableDivRef.current) {
      editableDivRef.current.innerHTML = preDiffHtml;
      setHtmlContent(preDiffHtml);
      setContent(preDiffHtml);
      localStorage.setItem("textBoxContent", preDiffHtml);
    }
    setPreDiffHtml(null);
    setDiffActiveForVersion(null);
  };

  function stripTags(html = "") {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  }

  function flattenEditorText(rootEl) {
    const walker = document.createTreeWalker(
      rootEl,
      NodeFilter.SHOW_TEXT,
      null
    );
    let node;
    let plain = "";
    const posToLoc = [];

    while ((node = walker.nextNode())) {
      const val = node.nodeValue || "";
      for (let i = 0; i < val.length; i++) {
        plain += val[i];
        posToLoc.push({ node, offset: i });
      }
    }
    return { plain, posToLoc };
  }

  function normalizeWithMap(input) {
    const out = [];
    const map = [];
    let lastWasSpace = false;
    for (let i = 0; i < input.length; i++) {
      let ch = input[i];
      if (/\s|\u00A0/.test(ch)) {
        if (lastWasSpace) continue;
        ch = " ";
        lastWasSpace = true;
      } else {
        lastWasSpace = false;
      }
      const lower = ch.toLowerCase();
      out.push(lower);
      map.push(i);
    }
    return { norm: out.join(""), map };
  }

  const handleSaveComment = async () => {
    try {
      if (!currentArticleId) {
        alert("Open or save an article first.");
        return;
      }
      // Note: pendingSelection can be null for a generic comment
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id || null;
      if (!uid) {
        alert("You must be signed in to comment.");
        return;
      }

      await createComment({
        article_id: currentArticleId,
        exact_text: pendingSelection?.exactText || "",
        occurrence_index: pendingSelection?.occurrenceIndex || 0,
        body: commentBody.trim(),
        created_by: uid,
        status: "open",
      });

      setIsCommentModalOpen(false);
      setPendingSelection(null);
      setCommentBody("");

      window.dispatchEvent(
        new CustomEvent("comments:updated", {
          detail: { articleId: currentArticleId },
        })
      );
    } catch (e) {
      console.error("Failed to save comment:", e);
      alert("Failed to save comment.");
    }
  };

  const handleCancelComment = () => {
    setIsCommentModalOpen(false);
    setPendingSelection(null);
    setCommentBody("");
  };

  useEffect(() => {
    const savedContent = localStorage.getItem("textBoxContent");
    const savedMetaTags = localStorage.getItem("metaTagsContent");
    const savedArticleId = localStorage.getItem("currentArticleId");

    if (savedContent) {
      editableDivRef.current.innerHTML = savedContent;
      setHtmlContent(savedContent);
      setContent(savedContent);
      setLastSavedHTML(savedContent);
    }

    if (savedArticleId && savedArticleId !== "null") {
      setCurrentArticleId(savedArticleId);
      setEditorMode("existing");
    }

    if (savedMetaTags) {
      metaTagsDivRef.current.innerHTML = savedMetaTags;
    }

    const div = editableDivRef.current;
    div.addEventListener("paste", handlePaste);

    return () => {
      div.removeEventListener("paste", handlePaste);
      clearTimeout(debounceRef.current);
      clearTimeout(autosaveFlashTimer.current);
    };
  }, [setContent]);

  useEffect(() => {
    const meta = feedback?.metaTags;
    if (meta) {
      const metaTagsString = meta.replace(/</g, "&lt;").replace(/>/g, "&gt;");
      if (metaTagsDivRef.current)
        metaTagsDivRef.current.innerHTML = metaTagsString;
      localStorage.setItem("metaTagsContent", metaTagsString);
    } else {
      if (metaTagsDivRef.current) metaTagsDivRef.current.innerHTML = "";
      localStorage.removeItem("metaTagsContent");
    }
  }, [feedback]);

  useEffect(() => {
    if (!pendingVersion) return;
    const v = pendingVersion;
    const html = v.content_html || "";
    if (editableDivRef.current) {
      editableDivRef.current.innerHTML = html;
    }
    setHtmlContent(html);
    setContent(html);
    localStorage.setItem("textBoxContent", html);
    if (v.title) setCurrentTitle(v.title);
    emitCtx(currentArticleId, v.title || currentTitle);
    // So a subsequent Analyze updates THIS version's row, not whichever one
    // happened to be current before it was opened.
    setCurrentVersionId(v.id || null);
    // Restore this version's own score snapshot (null shows as "-" for
    // versions saved before this existed, or never analyzed).
    setFeedback?.((prev) => ({
      ...prev,
      overall: v.score,
      readability: v.readability_score,
      topicAuthority: v.topic_authority_score,
      contentValue: v.content_value_score,
      seo: v.seo_score,
    }));
    onVersionApplied?.();
  }, [pendingVersion]);

  // Listen for "Add Comment" requests from Sidebar
  useEffect(() => {
    function onNewComment() {
      if (!currentArticleId) {
        alert("Open or save an article first.");
        return;
      }
      const sel = getSelectionInEditor();
      // If no selection, we assume it's a "generic/global" comment.
      setPendingSelection(sel || null);
      setCommentBody("");
      setIsCommentModalOpen(true);
    }
    window.addEventListener("comments:new", onNewComment);
    return () => window.removeEventListener("comments:new", onNewComment);
  }, [currentArticleId]);

  // Listen for "Check Style Guide" requests from Sidebar2's Markup AI view
  useEffect(() => {
    async function onMarkupCheck() {
      try {
        const currentHtml = editableDivRef.current?.innerHTML ?? htmlContent;
        const checks = await checkMarkupAIAll(currentHtml);
        window.dispatchEvent(
          new CustomEvent("markupai:result", { detail: { checks } })
        );
      } catch (e) {
        console.error("Markup AI check failed:", e);
        window.dispatchEvent(
          new CustomEvent("markupai:result", {
            detail: { checks: {}, error: "Markup AI check failed. Please try again." },
          })
        );
      }
    }
    window.addEventListener("markupai:check", onMarkupCheck);
    return () => window.removeEventListener("markupai:check", onMarkupCheck);
  }, [htmlContent]);

  useEffect(() => {
    // ✅ Listen for "Edit Brief" request from Sidebar
    function onEditBriefRequest(e) {
      // The event can carry the brief in detail, or we can use local state
      setIsModalOpen(true);
    }
    window.addEventListener("generation:edit-brief", onEditBriefRequest);
    return () =>
      window.removeEventListener("generation:edit-brief", onEditBriefRequest);
  }, []);

  // ✅ UPDATED: Handle toggling behavior for comment highlights
  useEffect(() => {
    function onShow(e) {
      const { exact_text, occurrence_index = 0 } = e.detail || {};
      if (!exact_text) return;

      // 1. If same comment is clicked again, toggle OFF
      if (
        currentHighlightRef.current &&
        currentHighlightRef.current.text === exact_text &&
        currentHighlightRef.current.index === occurrence_index
      ) {
        clearHighlights();
        currentHighlightRef.current = null;
        return;
      }

      // 2. Otherwise highlight new one (clearing old one automatically happens via highlightExactText)
      const span = highlightExactText(
        exact_text,
        occurrence_index,
        "comment-highlight"
      );
      if (!span) {
        alert("Could not find that text in the editor.");
        return;
      }
      scrollSpanIntoEditor(span);
      currentHighlightRef.current = {
        text: exact_text,
        index: occurrence_index,
      };
    }

    window.addEventListener("comments:show", onShow);
    return () => window.removeEventListener("comments:show", onShow);
  }, []);

  useEffect(() => {
    function onClearComments() {
      clearHighlights();
    }
    window.addEventListener("comments:clear", onClearComments);
    return () => window.removeEventListener("comments:clear", onClearComments);
  }, []);

  useEffect(() => {
    function onClearVersions() {
      restoreEditorFromSnapshot();
    }
    window.addEventListener("versions:clearChanges", onClearVersions);
    return () =>
      window.removeEventListener("versions:clearChanges", onClearVersions);
  }, [restoreEditorFromSnapshot]);

  useEffect(() => {
    async function onShowChanges(e) {
      let { version_id, article_id, version_no } = e.detail || {};
      if (!version_id) {
        alert("Missing version id.");
        return;
      }
      if (diffActiveForVersion === version_id) {
        restoreEditorFromSnapshot();
        return;
      }
      if (diffActiveForVersion && preDiffHtml != null) {
        restoreEditorFromSnapshot();
      }
      if (!article_id || !version_no) {
        try {
          const vfull = await getArticleVersion(version_id);
          if (!vfull) {
            alert("Version not found.");
            return;
          }
          article_id = article_id || vfull.article_id;
          version_no = version_no || vfull.version_no;
        } catch (err) {
          console.error("Failed to fetch version details:", err);
          alert("Failed to fetch version details.");
          return;
        }
      }

      try {
        const v = await getArticleVersion(version_id);
        const prev = await getPreviousVersion(article_id, version_no);
        if (!v || !prev) {
          alert("Previous version not found.");
          return;
        }

        const newHtml = v.content_html || "";
        const oldHtml = prev.content_html || "";

        if (editableDivRef.current && preDiffHtml == null) {
          setPreDiffHtml(editableDivRef.current.innerHTML);
        }

        const parts = diffWords(oldHtml, newHtml);
        const highlighted = parts
          .map((p) => {
            if (p.added) return `<span class="diff-added">${p.value}</span>`;
            if (p.removed)
              return `<span class="diff-removed">${p.value}</span>`;
            return p.value;
          })
          .join("");

        if (editableDivRef.current) {
          editableDivRef.current.innerHTML = highlighted;
        }

        setDiffActiveForVersion(version_id);
      } catch (err) {
        console.error("ShowChanges failed:", err);
        alert("Failed to compute/show changes.");
      }
    }

    window.addEventListener("versions:showChanges", onShowChanges);
    return () =>
      window.removeEventListener("versions:showChanges", onShowChanges);
  }, [diffActiveForVersion, preDiffHtml, setContent]);

  useEffect(() => {
    function onOpenFromFM(e) {
      const article = e.detail;
      window.dispatchEvent(new CustomEvent("versions:clearChanges"));

      setEditorMode("existing");
      setCurrentArticleId(article.id);
      setCurrentVersionId(null); // resolved lazily (latest version) on next Analyze
      setCurrentTitle(article.title || "Untitled Article");
      setCurrentProjectId(article.project_id || null);
      emitCtx(article.id, article.title || "Untitled Article");

      if (article.progress) setProgress(article.progress);

      setFeedback?.({
        overall: article.score ?? null,
        readability: article.readability_score ?? null,
        topicAuthority: article.topic_authority_score ?? null,
        contentValue: article.content_value_score ?? null,
        seo: article.seo_score ?? null,
        improvements: null,
      });

      // ✅ Update brief from loaded article
      if (article.content_brief) {
        setLocalBrief(article.content_brief);
        if (setBrief) setBrief(article.content_brief);
        window.dispatchEvent(
          new CustomEvent("brief:update", { detail: article.content_brief })
        );
      } else {
        setLocalBrief(null);
        if (setBrief) setBrief(null);
        window.dispatchEvent(new CustomEvent("brief:update", { detail: null }));
      }

      const html = article.content_html || "";
      if (editableDivRef.current) {
        editableDivRef.current.innerHTML = html;
      }
      setHtmlContent(html);
      setContent(html);
      localStorage.setItem("textBoxContent", html);
      setLastSavedHTML(html);

      if (article.meta_tags) {
        const metaTagsString = article.meta_tags
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        if (metaTagsDivRef.current)
          metaTagsDivRef.current.innerHTML = metaTagsString;
        localStorage.setItem("metaTagsContent", metaTagsString);
      } else {
        if (metaTagsDivRef.current) metaTagsDivRef.current.innerHTML = "";
        localStorage.removeItem("metaTagsContent");
      }

      window.dispatchEvent(
        new CustomEvent("versions:refresh", {
          detail: { article_id: article.id },
        })
      );
    }

    window.addEventListener("filemanager:openArticle", onOpenFromFM);
    return () =>
      window.removeEventListener("filemanager:openArticle", onOpenFromFM);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (e.target.closest?.(".selection-action-btn")) return;
      if (e.target.closest?.(".revise-tip")) return;
      if (e.target.closest?.(".improvement-highlight")) return;

      setSelectionAction(null);
    };

    window.addEventListener("mousedown", onClickOutside);
    return () => window.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    function onIssueShow(e) {
      const {
        category,
        index,
        exact_text,
        occurrence_index = 0,
        suggested_replacement,
        reason,
        severity,
      } = e.detail || {};
      if (!exact_text) return;

      const span = highlightExactText(
        exact_text,
        occurrence_index,
        "improvement-highlight"
      );
      if (!span) {
        alert("Could not find that text in the editor.");
        return;
      }

      scrollSpanIntoEditor(span, { smooth: false });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const spanRect = span.getBoundingClientRect();
          const tooltipWidth = 320;
          const padding = 8;
          let top = spanRect.top - padding;
          let left = spanRect.left;

          if (left + tooltipWidth + padding > window.innerWidth) {
            left = window.innerWidth - tooltipWidth - padding;
          }
          if (left < padding) left = padding;
          if (top < padding) {
            top = spanRect.bottom + padding;
          }

          setImprovementTip({
            top,
            left,
            suggestion: suggested_replacement,
            reason,
            severity: severity || "low",
            categoryKey: category,
            index,
          });
        });
      });
    }

    window.addEventListener("issues:show", onIssueShow);
    return () => window.removeEventListener("issues:show", onIssueShow);
  }, []);

  // ✅ NEW: Handlers for Accept / Revert revision
  const handleAcceptRevision = () => {
    if (!revisionWidget) return;
    const el = document.getElementById(revisionWidget.id);
    if (el && editableDivRef.current) {
      // Unwrap: keep content, remove span
      el.replaceWith(...el.childNodes);
      handleTextChange(); // Triggers autosave logic
    }
    setRevisionWidget(null);
  };

  const handleRevertRevision = () => {
    if (!revisionWidget) return;
    const el = document.getElementById(revisionWidget.id);
    if (el && editableDivRef.current) {
      // Restore old HTML content entirely
      el.outerHTML = revisionWidget.originalSpanContent;
      handleTextChange();
    }
    setRevisionWidget(null);
  };

  const StatusPopup = ({ message }) => {
    if (!message) return null;
    return (
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          backgroundColor: "#111827",
          color: "white",
          padding: "12px 20px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 10000,
          fontWeight: "500",
        }}
      >
        {message}
      </div>
    );
  };

  return (
    <>
      <TextBoxWrapper>
        <DiffStyles />

        <Toolbar>
          {/* ✅ CONDITIONAL RENDER BASED ON AI FLAG */}
          {organization?.ai_enabled ? (
            <>
              <ToolbarButton
                onClick={handleGenerateClick}
                style={{
                  backgroundColor: "#00876e",
                  color: "white",
                  padding: "0 16px",
                  border: "none",
                  borderRadius: "5px",
                  fontSize: "13px",
                  marginRight: "15px",
                }}
              >
                Generate
              </ToolbarButton>
              <ToolbarButton
                onClick={handleAnalyzeClick}
                style={{
                  backgroundColor: "white",
                  color: "#00876e",
                  border: "1px solid #00876e",
                  borderRadius: "5px",
                  fontSize: "13px",
                  padding: "0 16px",
                }}
              >
                Analyze
              </ToolbarButton>
              <select
                value={llmProvider}
                onChange={(e) => setLlmProvider(e.target.value)}
                style={{
                  padding: "6px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: "13px",
                  marginLeft: "8px",
                }}
              >
                <option value="openai">ChatGPT</option>
                <option value="anthropic">Claude</option>
              </select>
            </>
          ) : (
            <ToolbarButton
              onClick={handleDownload}
              style={{
                backgroundColor: "#00876e",
                color: "white",
                padding: "0 16px",
                border: "none",
                borderRadius: "5px",
                fontSize: "13px",
                marginRight: "15px",
              }}
            >
              Download
            </ToolbarButton>
          )}

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <label style={{ fontSize: 12 }}>Status:</label>
            <select
              value={getDisplayStatus(progress, organization?.ai_enabled)}
              onChange={(e) => updateProgress(e.target.value)}
              style={{
                padding: "6px 10px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: "14px",
              }}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.value}
                </option>
              ))}
            </select>
          </div>
        </Toolbar>
        <Toolbar
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "5px",
            padding: "10px",
          }}
        >
          <ToolbarButton
            className="toolbar-button"
            onClick={() => execCommand("bold")}
            title="Bold"
          >
            B
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => execCommand("italic")}
            title="Italic"
          >
            I
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => execCommand("underline")}
            title="Underline"
          >
            U
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={handleInsertLink}
            title="Insert Link"
          >
            <img
              src="/link.png"
              style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={handleRemoveLink}
            title="Remove Link"
            style={{ color: "#ef4444" }}
          >
            <img
              src="/unlink.png"
              style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          {/* Format Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12 }}>Tags</label>
            <select
              defaultValue=""
              onMouseDown={saveSelection}
              onChange={(e) => {
                if (e.target.value) {
                  execCommand("formatBlock", e.target.value);
                  e.target.value = ""; // Reset dropdown after applying format
                }
              }}
              style={{
                padding: "6px 10px",
                marginRight: "10px",
                fontSize: "14px",
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                cursor: "pointer",
              }}
              title="Text Format"
            >
              <option value="" disabled>
                Select...
              </option>
              <option value="<p>">Normal</option>
              <option value="<h1>">Heading 1</option>
              <option value="<h2>">Heading 2</option>
              <option value="<h3>">Heading 3</option>
              <option value="<h4>">Heading 4</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12 }}>Size</label>
            <select
              value={fontSizePx}
              onMouseDown={saveSelection}
              onChange={(e) => applyFontSize(parseInt(e.target.value, 10))}
              style={{
                padding: "6px 10px",
                fontSize: "14px",
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: 6,
              }}
              title="Font size"
            >
              {[10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => execCommand("insertUnorderedList")}
            title="Bulleted List"
          >
            <img
              src="/list-text.png"
              style={{ width: "22px", height: "22px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => execCommand("insertOrderedList")}
            title="Numbered List"
          >
            <img
              src="/list-number.png"
              style={{ width: "22px", height: "22px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => onSaveArticle("save")}
            title="New Version"
            disabled={!currentArticleId}
          >
            <img
              src="/new-version.png"
              style={{ width: "22px", height: "22px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => onSaveArticle("saveAs")}
            title="Save As"
          >
            <img
              src="/save-file.png"
              style={{ width: "22px", height: "22px", verticalAlign: "middle" }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={() => setFileManagerOpen(true)}
            title="Open"
          >
            <img
              src="/open-file.png"
              style={{
                width: "22px",
                height: "22px",
                verticalAlign: "middle",
              }}
            />
          </ToolbarButton>
          <ToolbarButton
            className="toolbar-button"
            onClick={handleNewArticle}
            title="New"
          >
            <img
              src="/blank-page.png"
              style={{
                width: "22px",
                height: "22px",
                verticalAlign: "middle",
              }}
            />
          </ToolbarButton>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isAutosaving && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: "black",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                }}
              >
                Saving…
              </span>
            )}

            {autosaveFlash && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: "black",
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                }}
              >
                Article autosaved
              </span>
            )}
          </div>
        </Toolbar>
        <div style={{ position: "relative" }}>
          <EditableDiv
            contentEditable
            ref={editableDivRef}
            onInput={handleTextChange}
            onMouseUp={(e) => {
              handleEditorMouseUp(e);
              saveSelection();
            }}
            onKeyUp={saveSelection}
            onMouseDown={() => {
              // ✅ FIX: Clear highlights if clicking inside editor
              if (currentHighlightRef.current) {
                clearHighlights();
                currentHighlightRef.current = null;
              }
              setTimeout(saveSelection, 0);
            }}
            data-placeholder="Paste your article here..."
          ></EditableDiv>

          {selectionAction && (
            <button
              className="selection-action-btn"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={() => {
                if (!selectedRange) {
                  alert("No selection range found. Please re-select the text.");
                  return;
                }
                const range = selectedRange;
                const highlightSpan = document.createElement("span");
                highlightSpan.className = "improvement-highlight";
                highlightSpan.style.background = "rgba(0, 135, 110, 0.2)";
                highlightSpan.style.borderRadius = "4px";
                highlightSpan.style.padding = "0 2px";

                try {
                  range.surroundContents(highlightSpan);
                } catch (e) {
                  const contents = range.extractContents();
                  highlightSpan.appendChild(contents);
                  range.insertNode(highlightSpan);
                }
                scrollSpanIntoEditor(highlightSpan, { smooth: false });
                const spanRect = highlightSpan.getBoundingClientRect();

                setReviseTip({
                  top: spanRect.top - 8,
                  left: spanRect.left,
                  exactText: selectionAction.exactText,
                  occurrenceIndex: 0,
                });
                setReviseInstruction("");
                setSelectionAction(null);
                setSelectedRange(null);
              }}
              style={{
                position: "fixed",
                top: selectionAction.top,
                left: selectionAction.left,
                zIndex: 2000,
                width: 30,
                height: 30,
                borderRadius: 999,
                border: "none",
                background: "#00876e",
                color: "white",
                cursor: "pointer",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
              title="Suggest an edit"
            >
              ✨
            </button>
          )}

          {improvementTip && (
            <div
              style={{
                position: "fixed",
                top: improvementTip.top,
                left: improvementTip.left,
                transform: "translateY(-100%)",
                background: "#111827",
                color: "#fff",
                padding: "8px 10px",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                zIndex: 1000,
                maxWidth: 320,
                fontSize: 13,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 999,
                    padding: "2px 8px",
                    textTransform: "uppercase",
                    ...(improvementTip.severity.toLowerCase() === "high"
                      ? { background: "#fee2e2", color: "#991b1b" }
                      : improvementTip.severity.toLowerCase() === "medium"
                      ? { background: "#ffedd5", color: "#9a3412" }
                      : { background: "#dcfce7", color: "#14532d" }),
                  }}
                >
                  {improvementTip.severity} issue
                </span>
                <button
                  onClick={() => setImprovementTip(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#9ca3af",
                    cursor: "pointer",
                    fontSize: 14,
                    marginLeft: 8,
                  }}
                >
                  ×
                </button>
              </div>

              {improvementTip.reason && (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  {improvementTip.reason}
                </div>
              )}

              {improvementTip.suggestion && (
                <div style={{ marginBottom: 8 }}>
                  <strong>Suggested change:</strong>
                  <div style={{ marginTop: 2, fontSize: 13 }}>
                    {improvementTip.suggestion}
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  const editor = editableDivRef.current;
                  if (!editor) return;
                  const span = editor.querySelector(".improvement-highlight");
                  if (!span) {
                    alert("Could not find the highlighted text to replace.");
                    return;
                  }
                  const replacement = improvementTip.suggestion || "";
                  span.outerHTML = replacement;
                  const newHtml = editor.innerHTML;
                  setHtmlContent(newHtml);
                  setContent(newHtml);
                  localStorage.setItem("textBoxContent", newHtml);
                  if (
                    onImprovementApplied &&
                    improvementTip?.categoryKey != null
                  ) {
                    onImprovementApplied({
                      categoryKey: improvementTip.categoryKey,
                      index: improvementTip.index,
                    });
                  }
                  // Sidebar2's Markup AI checks live outside `feedback`, so they
                  // aren't covered by onImprovementApplied above — remove the
                  // applied issue from that list too (no-ops for Scores categories).
                  window.dispatchEvent(
                    new CustomEvent("markupai:issueApplied", {
                      detail: {
                        key: improvementTip.categoryKey,
                        index: improvementTip.index,
                      },
                    })
                  );
                  clearHighlights();
                  setImprovementTip(null);
                }}
              >
                Apply change
              </button>
            </div>
          )}

          {/* ✅ Accept / Revert Widget */}
          {revisionWidget && (
            <div
              style={{
                position: "fixed",
                top: revisionWidget.top,
                left: revisionWidget.left,
                zIndex: 3000,
                background: "#111827",
                color: "#fff",
                padding: "12px 16px", // Increased padding
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                fontSize: 13,
                display: "flex",
                flexDirection: "column", // Stack vertical
                gap: 8,
                minWidth: 200,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Would you like to accept the changes?
              </div>
              <div
                style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
              >
                <button
                  onClick={handleAcceptRevision}
                  style={{
                    background: "#22c55e",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 10px",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={handleRevertRevision}
                  style={{
                    background: "#6b7280",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 10px",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Revert
                </button>
              </div>
            </div>
          )}

          {reviseTip && (
            <div
              className="revise-tip"
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: reviseTip.top,
                left: reviseTip.left,
                transform: "translateY(-100%)",
                background: "#111827",
                color: "#fff",
                padding: "10px 12px",
                borderRadius: 10,
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                zIndex: 3000,
                width: 360,
                fontSize: 13,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>Suggest an edit</strong>
                <button
                  onClick={() => {
                    setReviseTip(null);
                    setReviseInstruction("");
                    clearHighlights();
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#9ca3af",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 8, marginBottom: 10, color: "#d1d5db" }}>
                Tell UG AI what to do with this section:
              </div>

              <textarea
                rows={3}
                value={reviseInstruction}
                onChange={(e) => setReviseInstruction(e.target.value)}
                placeholder='e.g. "Add more substance and include one example."'
                style={{
                  width: "100%",
                  borderRadius: 8,
                  border: "1px solid #374151",
                  padding: 8,
                  fontSize: 13,
                  background: "#0b1220",
                  color: "white",
                  resize: "none",
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 10,
                }}
              >
                <button
                  disabled={!reviseInstruction.trim() || isRevising}
                  onClick={async () => {
                    const editor = editableDivRef.current;
                    if (!editor) return;
                    const span = editor.querySelector(".improvement-highlight");
                    if (!span) {
                      alert(
                        "Could not find the highlighted section to revise."
                      );
                      return;
                    }
                    try {
                      setIsRevising(true);
                      beginGenerating?.("Revising selected section...");

                      // 1. Capture original HTML (inner content of the highlighted span)
                      const originalSpanContent = span.innerHTML;

                      const contextHtml = editor.innerHTML;
                      const revisedHtml = await reviseSelection({
                        selectedText: reviseTip.exactText,
                        instruction: reviseInstruction.trim(),
                        contextHtml,
                      });

                      if (!revisedHtml) {
                        alert("No revision returned.");
                        return;
                      }

                      // 2. Generate Revision ID and Replace
                      const revId = "rev-" + Date.now();

                      // Replace the span completely with a new styled span
                      span.outerHTML = `<span id="${revId}" class="ai-proposed-change">${revisedHtml}</span>`;

                      const newHtml = editor.innerHTML;
                      setHtmlContent(newHtml);
                      setContent(newHtml);
                      localStorage.setItem("textBoxContent", newHtml);

                      clearHighlights();
                      setReviseTip(null);
                      setReviseInstruction("");

                      // 3. Show Acceptance Widget
                      requestAnimationFrame(() => {
                        const newSpan = document.getElementById(revId);
                        if (newSpan) {
                          const rect = newSpan.getBoundingClientRect();
                          setRevisionWidget({
                            id: revId,
                            originalSpanContent,
                            top: rect.bottom + 8,
                            left: rect.left,
                          });
                        }
                      });
                    } catch (err) {
                      console.error(err);
                      alert("Failed to revise selection.");
                    } finally {
                      endGenerating?.();
                      setIsRevising(false);
                    }
                  }}
                  style={{
                    background: "#00876e",
                    border: "none",
                    color: "white",
                    padding: "8px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    opacity: !reviseInstruction.trim() || isRevising ? 0.6 : 1,
                  }}
                >
                  {isRevising ? "Revising..." : "Apply revision"}
                </button>
              </div>
            </div>
          )}
        </div>

        <h3 style={{ marginTop: "40px" }}>Optimal Meta Tags</h3>
        <EditableDiv
          style={{
            height: "30vh",
            marginTop: "20px",
            maxWidth: "100%",
            whiteSpace: "pre", // Prevent meta tags from wrapping
            overflowX: "scroll",
          }}
          contentEditable
          ref={metaTagsDivRef}
          data-placeholder="Meta tags will be displayed here after analysis..."
          data-gramm="false"
          spellCheck="false"
        ></EditableDiv>

        <GenerateModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          initialValues={localBrief || brief || {}} // ✅ Pre-fill with current brief
          currentUser={{ id: profile.id, role: profile.role }}
          projects={projects || []}
        />
      </TextBoxWrapper>
      <FileManager
        isOpen={fileManagerOpen}
        onClose={() => setFileManagerOpen(false)}
        beginGenerating={beginGenerating}
        endGenerating={endGenerating}
        orgId={profile?.company_id}
        currentUserId={profile?.id}
        currentUserRole={profile?.role}
        organization={organization}
        profile={profile}
        onOpenArticle={(article) => {
          window.dispatchEvent(new CustomEvent("versions:clearChanges"));
          setEditorMode("existing");
          setCurrentArticleId(article.id);
          setCurrentVersionId(null); // resolved lazily (latest version) on next Analyze
          localStorage.setItem("currentArticleId", article.id);
          setCurrentTitle(article.title || "Untitled Article");
          setCurrentProjectId(article.project_id || null);
          emitCtx(article.id, article.title || "Untitled Article");

          const html = article.content_html || "";

          if (editableDivRef.current) {
            editableDivRef.current.innerHTML = html;
          }
          setHtmlContent(html);
          setContent(html);
          localStorage.setItem("textBoxContent", html);
          setLastSavedHTML(html);

          // ✅ Set local brief from loaded article
          if (article.content_brief) {
            setLocalBrief(article.content_brief);
            if (setBrief) setBrief(article.content_brief);
            // ✅ Fix: dispatch event so Sidebar2 updates even if props are lagging
            window.dispatchEvent(
              new CustomEvent("brief:update", { detail: article.content_brief })
            );
          } else {
            setLocalBrief(null);
            if (setBrief) setBrief(null);
            window.dispatchEvent(
              new CustomEvent("brief:update", { detail: null })
            );
          }

          if (article.meta_tags) {
            const metaTagsString = article.meta_tags
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
            metaTagsDivRef.current.innerHTML = metaTagsString;
            localStorage.setItem("metaTagsContent", metaTagsString);
          } else {
            metaTagsDivRef.current.innerHTML = "";
            localStorage.removeItem("metaTagsContent");
          }
        }}
      />
      <StatusPopup message={statusMessage} />
      <HighlightStyle />

      {isCommentModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 10,
              width: 480,
              maxWidth: "95vw",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Add Comment</h3>
            {/* ✅ CONDITIONAL: Only show selection info if it exists */}
            {pendingSelection?.exactText && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                On selection:{" "}
                <em>
                  {pendingSelection.exactText.slice(0, 120)}
                  {pendingSelection.exactText.length > 120 ? "…" : ""}
                </em>
              </div>
            )}
            <textarea
              rows={5}
              placeholder="Describe the requested change…"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              style={{
                width: "100%",
                padding: 10,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 10,
                justifyContent: "flex-end",
              }}
            >
              <button onClick={handleCancelComment}>Cancel</button>
              <button
                onClick={handleSaveComment}
                style={{
                  background: "#111827",
                  color: "#fff",
                  padding: "6px 12px",
                  borderRadius: 8,
                }}
              >
                Save Comment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TextBox;
